/* ========================================================================== 
   SFFLABS SHARED CATALOG ROUTE + PORTABLE BUILD CONTRACT
   Backend catalog IDs and case.slug are authoritative. Name slugification is
   retained only for custom/legacy input recovery.
   ========================================================================== */
(function initSffContracts(global) {
  'use strict';

  if (global.SFFContracts?.version === '20260822-routebridge3') return;

  const VERSION = '20260822-routebridge3';
  const CASE_ROUTE_SCHEMA = 2;
  const BUILD_SCHEMA = 2;
  const IDENTITY_SCHEMA = 1;

  function safeText(value, max = 220) {
    if (value === undefined || value === null || value === '') return null;
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    const text = String(value).trim();
    return text && text.length <= max ? text : null;
  }

  function semanticPlusText(value) {
    return String(value || '')
      .replace(/(^|[^\p{L}\p{N}])[+＋]/gu, '$1 plus ')
      .replace(/[+＋](?=$|[^\p{L}\p{N}])/gu, ' plus ');
  }

  /** Legacy/custom fallback only. Catalog case records must consume record.slug. */
  function fallbackSlug(value) {
    const raw = semanticPlusText(value).toLowerCase().trim();
    if (!raw) return 'build';
    return raw
      .replace(/[^a-z0-9\s_-]+/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'build';
  }

  function normalizeRouteSlug(value) {
    const raw = String(value || '').trim().toLowerCase().replace(/^\/+|\/+$/g, '');
    return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(raw) ? raw : '';
  }

  function caseRouteSlug(recordOrValue, fallbackName = '') {
    if (recordOrValue && typeof recordOrValue === 'object' && !Array.isArray(recordOrValue)) {
      for (const key of ['slug', 'case_slug', 'db_slug']) {
        const slug = normalizeRouteSlug(recordOrValue[key]);
        if (slug) return slug;
      }
      return fallbackSlug(recordOrValue.name || fallbackName);
    }
    const supplied = normalizeRouteSlug(recordOrValue);
    if (supplied) return supplied;
    return fallbackSlug(recordOrValue || fallbackName);
  }

  function validCatalogId(value) {
    const id = String(value || '').trim().toLowerCase();
    return /^[a-f0-9]{24}$/.test(id) ? id : '';
  }

  function catalogIdOf(record) {
    if (!record || typeof record !== 'object') return '';
    return validCatalogId(record.catalogId || record.catalog_id || record.id);
  }

  function encodeUtf8Base64Url(text) {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 0x8000) {
      binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function decodeUtf8Base64Url(value) {
    let base64 = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
    while (base64.length % 4) base64 += '=';
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  }

  function safeNumberString(value, min, max) {
    if (value === undefined || value === null || value === '') return undefined;
    const num = Number(value);
    if (!Number.isFinite(num) || num < min || num > max) return null;
    return String(value);
  }

  function validateRequirements(parsed = {}) {
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const fields = {
      mobo: safeText(parsed.m ?? parsed.mobo, 32) ?? undefined,
      psu: safeText(parsed.p ?? parsed.psu, 32) ?? undefined,
      vol: safeNumberString(parsed.v ?? parsed.vol, 0.1, 100),
      gpuL: safeNumberString(parsed.gl ?? parsed.gpuL, 0, 1000),
      gpuT: safeNumberString(parsed.gt ?? parsed.gpuT, 0, 300),
      gpuH: safeNumberString(parsed.gh ?? parsed.gpuH, 0, 500),
      pcie: safeNumberString(parsed.pc ?? parsed.pcie, 0, 10),
      cool: safeText(parsed.c ?? parsed.cool, 24) ?? undefined,
      cpuH: safeNumberString(parsed.ch ?? parsed.cpuH, 0, 500),
      rad: safeNumberString(parsed.r ?? parsed.rad, 0, 600),
      fan: safeText(parsed.f ?? parsed.fan, 32) ?? undefined,
      ramH: safeNumberString(parsed.rh ?? parsed.ramH, 0, 150),
      ramModules: safeNumberString(parsed.rn ?? parsed.ramModules, 1, 8),
      search: safeText(parsed.s ?? parsed.search, 220) ?? undefined,
      gpuSearch: safeText(parsed.gs ?? parsed.gpuSearch, 220) ?? undefined,
      cpuSearch: safeText(parsed.cs ?? parsed.cpuSearch, 220) ?? undefined,
      aioSearch: safeText(parsed.as ?? parsed.aioSearch, 220) ?? undefined,
      moboSearch: safeText(parsed.ms ?? parsed.moboSearch, 220) ?? undefined,
      ramSearch: safeText(parsed.rs ?? parsed.ramSearch, 220) ?? undefined,
      report_id: safeText(parsed.rep ?? parsed.report_id, 160) ?? undefined
    };
    return Object.values(fields).some(value => value === null) ? null : fields;
  }

  function normalizeRef(raw) {
    if (!raw) return null;
    if (typeof raw === 'string') {
      const id = validCatalogId(raw);
      return id ? { id } : null;
    }
    if (typeof raw !== 'object' || Array.isArray(raw)) return null;
    const id = validCatalogId(raw.id || raw.catalogId || raw.catalog_id);
    if (id) {
      const ref = { id };
      const modules = Number(raw.n ?? raw.modules);
      if (Number.isFinite(modules) && modules > 0) ref.modules = Math.max(1, Math.min(8, Math.round(modules)));
      return ref;
    }
    if (raw.custom === true) {
      const d = raw.dims || {};
      const l = Number(d.l), w = Number(d.w), h = Number(d.h);
      if ([l, w, h].some(v => !Number.isFinite(v) || v < 0 || v > 2000)) return null;
      const ref = { custom: true, label: safeText(raw.label, 160) || 'Custom', dims: { l, w, h } };
      const modules = Number(raw.n ?? raw.modules);
      if (Number.isFinite(modules) && modules > 0) ref.modules = Math.max(1, Math.min(8, Math.round(modules)));
      return ref;
    }
    return null;
  }

  function normalizeBuildPayload(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const schema = Number(raw.v ?? raw.schema);
    const identitySchema = Number(raw.i ?? raw.identitySchema);
    if (schema !== BUILD_SCHEMA || identitySchema !== IDENTITY_SCHEMA) return null;
    const h = raw.h || raw.hardware || {};
    const requirements = validateRequirements(raw.r || raw.requirements || {});
    if (!requirements) return null;
    const ramRaw = h.r || h.ram;
    const ram = normalizeRef(ramRaw);
    if (ram && ramRaw && typeof ramRaw === 'object') {
      ram.modules = Math.max(1, Math.min(8, Number(ramRaw.n ?? ramRaw.modules ?? requirements.ramModules) || 2));
    }
    return {
      schema: BUILD_SCHEMA,
      identitySchema: IDENTITY_SCHEMA,
      case: normalizeRef(raw.c || raw.case),
      modeId: safeText(raw.m ?? raw.modeId, 120),
      fixedStep: safeText(raw.f ?? raw.fixedStep, 120),
      hardware: {
        motherboard: normalizeRef(h.mb || h.motherboard),
        gpu: normalizeRef(h.g || h.gpu),
        psu: normalizeRef(h.p || h.psu),
        aircooler: normalizeRef(h.ac || h.aircooler),
        aio: normalizeRef(h.a || h.aio),
        ram
      },
      requirements
    };
  }

  function compactRef(ref) {
    if (!ref) return undefined;
    const id = validCatalogId(ref.id);
    if (id) {
      const modules = Number(ref.modules);
      return Number.isFinite(modules) && modules > 0 ? { id, n: Math.max(1, Math.min(8, Math.round(modules))) } : id;
    }
    if (ref.custom === true) {
      const out = { custom: true, label: safeText(ref.label, 160) || 'Custom', dims: ref.dims };
      const modules = Number(ref.modules);
      if (Number.isFinite(modules) && modules > 0) out.n = Math.max(1, Math.min(8, Math.round(modules)));
      return out;
    }
    return undefined;
  }

  function compactRequirements(requirements = {}) {
    const normalized = validateRequirements(requirements);
    if (!normalized) return undefined;
    const out = {
      m: normalized.mobo, p: normalized.psu, v: normalized.vol,
      gl: normalized.gpuL, gt: normalized.gpuT, gh: normalized.gpuH,
      pc: normalized.pcie, c: normalized.cool, ch: normalized.cpuH,
      r: normalized.rad, f: normalized.fan, rh: normalized.ramH,
      rn: normalized.ramModules, s: normalized.search, gs: normalized.gpuSearch,
      cs: normalized.cpuSearch, as: normalized.aioSearch, ms: normalized.moboSearch,
      rs: normalized.ramSearch, rep: normalized.report_id
    };
    Object.keys(out).forEach(key => out[key] === undefined && delete out[key]);
    return Object.keys(out).length ? out : undefined;
  }

  function compactBuildPayload(payload) {
    const normalized = normalizeBuildPayload(payload);
    if (!normalized) return null;
    const h = normalized.hardware || {};
    const out = {
      v: BUILD_SCHEMA,
      i: IDENTITY_SCHEMA,
      c: compactRef(normalized.case),
      m: normalized.modeId && normalized.modeId !== 'default' ? normalized.modeId : undefined,
      f: normalized.fixedStep || undefined,
      h: {
        mb: compactRef(h.motherboard), g: compactRef(h.gpu), p: compactRef(h.psu),
        ac: compactRef(h.aircooler), a: compactRef(h.aio), r: compactRef(h.ram)
      },
      r: compactRequirements(normalized.requirements)
    };
    Object.keys(out.h).forEach(key => out.h[key] === undefined && delete out.h[key]);
    if (!Object.keys(out.h).length) delete out.h;
    Object.keys(out).forEach(key => out[key] === undefined && delete out[key]);
    return out;
  }

  function encodeJsonToken(value) {
    try { return encodeUtf8Base64Url(JSON.stringify(value)); } catch (_) { return ''; }
  }

  function decodeJsonToken(token) {
    if (!token || typeof token !== 'string' || token.length > 8192) return null;
    try { return JSON.parse(decodeUtf8Base64Url(token)); } catch (_) { return null; }
  }

  function encodeBuildToken(payload) {
    const compact = compactBuildPayload(payload);
    return compact ? encodeJsonToken(compact) : '';
  }

  function decodeBuildToken(token) {
    if (!token || typeof token !== 'string' || token.length > 8192) return null;
    return normalizeBuildPayload(decodeJsonToken(token));
  }

  function dbCasePath(caseRecordOrSlug, payload = null) {
    const slug = caseRouteSlug(caseRecordOrSlug);
    const token = payload ? encodeBuildToken(payload) : '';
    return `/db/${slug}/${token ? `${token}/` : ''}`;
  }

  function workbenchPath(caseRecordOrSlug, payload = null) {
    const slug = caseRouteSlug(caseRecordOrSlug);
    const token = payload ? encodeBuildToken(payload) : '';
    return `/3d/${slug}/${token ? `${token}/` : ''}`;
  }

  function comparePath(caseA, caseB, payload = null) {
    const pair = `${caseRouteSlug(caseA)}-vs-${caseRouteSlug(caseB)}`;
    const token = payload ? encodeBuildToken(payload) : '';
    return `/db/compare/${pair}/${token ? `${token}/` : ''}`;
  }

  function parse3dRoute(pathname = global.location?.pathname || '') {
    const parts = String(pathname || '').split('/').filter(Boolean);
    const index = parts.indexOf('3d');
    return index < 0 ? { slug: null, stateToken: null } : { slug: parts[index + 1] || null, stateToken: parts[index + 2] || null };
  }

  global.SFFContracts = Object.freeze({
    version: VERSION,
    CASE_ROUTE_SCHEMA,
    BUILD_SCHEMA,
    IDENTITY_SCHEMA,
    fallbackSlug,
    normalizeRouteSlug,
    caseRouteSlug,
    validCatalogId,
    catalogIdOf,
    normalizeRef,
    validateRequirements,
    normalizeBuildPayload,
    compactBuildPayload,
    encodeJsonToken,
    decodeJsonToken,
    encodeBuildToken,
    decodeBuildToken,
    dbCasePath,
    workbenchPath,
    comparePath,
    parse3dRoute
  });
})(globalThis);
